import Modifier from 'ember-modifier';
import type { ArgsFor } from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';

/**
 * Calls the given callback exactly once, when the element is first inserted.
 *
 * Typed so Glint can check invocations from strict-mode templates (`.gts`/`.gjs`).
 * Without a Signature, `Modifier` defaults to an empty positional tuple and Glint
 * rejects any invocation with "Expected 1 arguments, but got 2".
 *
 * Loose-mode `.hbs` templates are unaffected either way — Glint does not
 * type-check them — so the existing `{{render-modifiers/on-insert}}` call sites
 * keep working unchanged.
 */
export interface OnInsertSignature {
  Element: Element;
  Args: {
    Positional: [
      callback: (element: Element, ...args: unknown[]) => void,
      ...args: unknown[],
    ];
  };
}

function cleanup(instance: OnInsertModifier): void {
  instance.count = 0;
}

export default class OnInsertModifier extends Modifier<OnInsertSignature> {
  count = 0;

  // The first argument is the owner, not the element — the element is passed to
  // `modify`. The original JS named it `element`, which was misleading.
  constructor(owner: Owner, args: ArgsFor<OnInsertSignature>) {
    super(owner, args);
    registerDestructor(this, cleanup);
    this.count = 0;
  }

  // The modify method is called to apply the logic to the element
  modify(
    element: Element,
    [callback, ...args]: OnInsertSignature['Args']['Positional'],
  ): void {
    if (!element) {
      return;
    }
    if (this.count !== 0) return;
    this.count = this.count + 1;
    if (typeof callback === 'function') {
      callback(element, ...args);
    }
  }
}
